import { useEffect, useState, useCallback, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getModules, toggleModule, installModule, uninstallModule, updateModuleWidth } from "../services/api";
import { AuthContext } from "../context/AuthContext";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import ToggleSwitch from "../components/UI/ToggleSwitch";
import ConfirmModal from "../components/UI/ConfirmModal";
import DataTable from "../components/UI/DataTable";
import PageHeader from "../components/UI/PageHeader";
import { notify } from "../utils/notify";

// صفحة "Modules": سجل عام لأي جزء قابل للتفعيل/التعطيل أو التركيب في
// النظام — أجزاء الداشبورد دلوقتي، وأي جزء تاني مستقبلاً. كل صف هنا بيتحكم
// فيه بوابتين مستقلتين: تفعيل عام (يظهر هنا) وصلاحية خاصة بكل مستخدم/مجموعة
// (تتضبط من صفحة Permissions/Groups الموجودة بالفعل).
export default function Modules() {
  const { userPermissions } = useContext(AuthContext);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: "category", direction: "asc" });
  const [togglingId, setTogglingId] = useState(null);
  const [uninstallTarget, setUninstallTarget] = useState(null); // { id, name } | null
  const [uninstalling, setUninstalling] = useState(false);
  const [installOpen, setInstallOpen] = useState(false);
  const [installFile, setInstallFile] = useState(null);
  const [installing, setInstalling] = useState(false);
  const fileInputRef = useRef(null);

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const canManage = userPermissions?.includes("module_manage");
  const canInstall = userPermissions?.includes("module_install");

  const WIDTH_OPTIONS = [
    { value: 3, label: "Narrow (25%)" },
    { value: 4, label: "Small (33%)" },
    { value: 6, label: "Medium (50%)" },
    { value: 8, label: "Large (66%)" },
    { value: 12, label: "Full width" },
  ];

  // Every dashboard-category widget (built-in or installed extension) now
  // lives in the same personal drag/resize/group layout on the Dashboard
  // page, so Width applies uniformly to all of them — it's just the
  // starting size used the first time a given widget appears in a user's
  // own layout; after that, whatever that user drags/resizes wins.
  const widthApplies = (row) => row.category === "dashboard";

  const [widthUpdatingId, setWidthUpdatingId] = useState(null);
  const handleWidthChange = async (row, width) => {
    setWidthUpdatingId(row.id);
    try {
      const updated = await updateModuleWidth(token, row.id, width);
      setModules((prev) => prev.map((m) => (m.id === row.id ? updated : m)));
    } catch (err) {
      notify.error(err.message || "Failed to update width");
    } finally {
      setWidthUpdatingId(null);
    }
  };

  const fetchModules = useCallback(async () => {
    try {
      const data = await getModules(token);
      setModules(data || []);
    } catch (err) {
      notify.error(err.message || "Failed to load modules");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const sortedModules = [...modules].sort((a, b) => {
    let valA = a[sortConfig.key];
    let valB = b[sortConfig.key];
    if (typeof valA === "string") valA = valA.toLowerCase();
    if (typeof valB === "string") valB = valB.toLowerCase();
    if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
    if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const handleToggle = async (mod) => {
    if (!canManage) return;
    setTogglingId(mod.id);
    try {
      const updated = await toggleModule(token, mod.id);
      setModules((prev) => prev.map((m) => (m.id === mod.id ? updated : m)));
      notify.success(`${mod.name} ${updated.is_enabled ? "enabled" : "disabled"}`);
    } catch (err) {
      notify.error(err.message || "Failed to toggle module");
    } finally {
      setTogglingId(null);
    }
  };

  const confirmUninstall = async () => {
    if (!uninstallTarget) return;
    setUninstalling(true);
    try {
      await uninstallModule(token, uninstallTarget.id);
      notify.success("Extension uninstalled successfully");
      setModules((prev) => prev.filter((m) => m.id !== uninstallTarget.id));
      setUninstallTarget(null);
    } catch (err) {
      notify.error(err.message || "Failed to uninstall extension");
    } finally {
      setUninstalling(false);
    }
  };

  const handleInstallSubmit = async () => {
    if (!installFile) {
      notify.error("Choose a .zip package first");
      return;
    }
    setInstalling(true);
    try {
      await installModule(token, installFile);
      notify.success("Extension installed successfully");
      setInstallOpen(false);
      setInstallFile(null);
      fetchModules();
    } catch (err) {
      notify.error(err.data?.error || err.message || "Failed to install extension");
    } finally {
      setInstalling(false);
    }
  };

  const columns = [
    {
      key: "name",
      label: "Module",
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-black text-gray-800 text-sm tracking-tight">{row.name}</span>
          <span className="text-[10px] text-gray-400 italic font-medium">{row.description || "No description"}</span>
        </div>
      ),
    },
    {
      key: "category",
      label: "Category",
      render: (row) => (
        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-600 capitalize">
          {row.category}
        </span>
      ),
    },
    {
      key: "permission",
      label: "Required Permission",
      render: (row) => (
        <code className="text-[11px] text-gray-500 font-mono bg-gray-50 px-1.5 py-0.5 rounded">
          {row.permission}
        </code>
      ),
    },
    {
      key: "is_builtin",
      label: "Type",
      render: (row) => (
        <span
          className={`px-2 py-0.5 rounded text-[9px] font-black border ${
            row.is_builtin
              ? "bg-blue-50 border-blue-100 text-blue-600"
              : "bg-purple-50 border-purple-100 text-purple-600"
          }`}
        >
          {row.is_builtin ? "BUILT-IN" : "EXTENSION"}
        </span>
      ),
    },
    {
      key: "width",
      label: "Display Width",
      render: (row) =>
        widthApplies(row) ? (
          <select
            value={row.width || 4}
            disabled={!canManage || widthUpdatingId === row.id}
            onChange={(e) => handleWidthChange(row, Number(e.target.value))}
            className="text-xs font-medium border border-gray-100 rounded-lg px-2 py-1.5 bg-gray-50/80 focus:outline-none focus:border-[var(--primary-color)] disabled:opacity-60"
          >
            {WIDTH_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        ) : (
          <span className="text-[10px] text-gray-300 italic">n/a</span>
        ),
    },
    {
      key: "is_enabled",
      label: "Status",
      render: (row) => (
        <ToggleSwitch
          name={`module-${row.id}`}
          checked={row.is_enabled}
          onChange={() => handleToggle(row)}
        />
      ),
      className: togglingId ? "opacity-60 pointer-events-none" : "",
    },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      <ConfirmModal
        isOpen={!!uninstallTarget}
        title="Uninstall extension?"
        message={`This will remove "${uninstallTarget?.name}" and all its files. This can't be undone.`}
        onConfirm={confirmUninstall}
        onCancel={() => setUninstallTarget(null)}
        isLoading={uninstalling}
      />

      {installOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-[2rem] p-8 max-w-md w-full shadow-2xl">
            <h3 className="text-xl font-black text-gray-800 mb-2">Install Extension</h3>
            <p className="text-gray-400 text-xs mb-6 leading-relaxed">
              Upload a .zip package containing a <code className="bg-gray-50 px-1 rounded">manifest.json</code> and
              a self-contained static widget (its own index.html + assets). It will be rendered in an isolated
              frame — no server restart needed.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip"
              onChange={(e) => setInstallFile(e.target.files?.[0] || null)}
              className="w-full text-sm text-gray-600 mb-6 file:me-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-bold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
            />
            <div className="flex gap-3">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => {
                  setInstallOpen(false);
                  setInstallFile(null);
                }}
                disabled={installing}
              >
                Cancel
              </Button>
              <Button variant="primary" className="flex-1" onClick={handleInstallSubmit} loading={installing}>
                Install
              </Button>
            </div>
          </div>
        </div>
      )}

      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="text-[var(--primary-color)]">
            <ICONS.Modules className="w-10 h-10 shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-black tracking-tight" style={{ color: "var(--primary-color)" }}>
              Modules
            </h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium italic">
              Turn parts of the system on or off, or install new extensions
            </p>
          </div>
        </div>

        {canInstall && (
          <Button variant="primary" icon={ICONS.Install} onClick={() => setInstallOpen(true)} className="shadow-lg shadow-blue-500/20">
            Install Extension
          </Button>
        )}
      </PageHeader>

      {!canManage && (
        <div className="mb-6 px-4 py-3 rounded-xl bg-amber-50 border border-amber-100 text-amber-700 text-xs font-medium">
          You can view modules, but you need the <code className="font-mono">module_manage</code> permission to enable/disable them.
        </div>
      )}

      <div className="mb-6 px-4 py-3 rounded-xl bg-blue-50 border border-blue-100 text-blue-700 text-xs font-medium">
        Display Width is the starting size a widget gets the first time it appears on someone's dashboard. Each user
        can freely drag, resize and group their own widgets from the Dashboard's "Customize Layout" mode afterward —
        this is only the default.
      </div>

      <DataTable
        columns={columns}
        data={loading ? [] : sortedModules}
        sortConfig={sortConfig}
        onSort={requestSort}
        renderActions={(row, isMobile) =>
          !row.is_builtin && (
            <Button
              variant="danger"
              size="sm"
              icon={ICONS.DeleteUser}
              disabled={!canInstall}
              onClick={() => setUninstallTarget({ id: row.id, name: row.name })}
            >
              {isMobile && " Uninstall"}
            </Button>
          )
        }
        mobileRender={(row) => (
          <div className="flex justify-between items-center">
            <div className="flex flex-col">
              <span className="text-[10px] text-gray-400 uppercase">{row.category}</span>
              <h3 className="font-bold text-gray-800">{row.name}</h3>
            </div>
            <ToggleSwitch name={`module-mobile-${row.id}`} checked={row.is_enabled} onChange={() => handleToggle(row)} />
          </div>
        )}
      />
    </div>
  );
}
